import {isSameDay, isSameMonth, isToday as dateFnsIsToday} from 'date-fns';
import {GridItem, Text, ThemeColorTextPath} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {cva} from 'platform/shared';
import {match, Pattern} from 'ts-pattern';

import {useLayoutEffect, useRef} from 'react';

import {CalendarFormValue} from '../../types';

interface DayProps {
  date: Date;
  selectedDate: CalendarFormValue;
  activeMonth: Date;
  isDisabled?: boolean;
  isHovered?: boolean;
  isFocused?: boolean;
  onSelectDate?: (date: Date) => void;
  onHover?: (date: Date) => void;
  onFocus?: (date: Date) => void;
}

export function Day(props: DayProps) {
  const dayRef = useRef<HTMLDivElement>(null);
  const formatDate = useDateTimeFormatter();

  const isSelected = Array.isArray(props.selectedDate)
    ? (props.selectedDate[0] && isSameDay(props.selectedDate[0], props.date)) ||
      (props.selectedDate[1] && isSameDay(props.selectedDate[1], props.date))
    : props.selectedDate && isSameDay(props.selectedDate, props.date);

  const isToday = dateFnsIsToday(props.date);
  const isDifferentMonth = !isSameMonth(props.date, props.activeMonth);

  useLayoutEffect(() => {
    if (props.isFocused && dayRef.current) {
      dayRef.current.focus();
    }
  }, [props.isFocused]);

  const getDayTextColor = (): ThemeColorTextPath | undefined => {
    if (isDifferentMonth) {
      return 'tertiary';
    }
    if (isSelected) {
      return 'white';
    }
    if (isToday || props.isHovered || props.isFocused) {
      return 'link';
    }
    if (props.isDisabled) {
      return 'tertiary';
    }
    return undefined;
  };

  const color = getDayTextColor();
  const background = match<
    [boolean, boolean, boolean],
    'differentMonth' | 'selected' | 'highlighted' | 'default'
  >([isDifferentMonth, Boolean(isSelected), Boolean(props.isHovered || props.isFocused)])
    .with([true, Pattern.boolean, Pattern.boolean], () => 'differentMonth')
    .with([false, true, Pattern.boolean], () => 'selected')
    .with([false, false, true], () => 'highlighted')
    .otherwise(() => 'default');

  return (
    <GridItem>
      <div
        ref={dayRef}
        className={dayVariants({
          background,
          isDisabled: Boolean(props.isDisabled),
          isFocused: Boolean(props.isFocused),
          isHoverable: !props.isDisabled && !isSelected,
        })}
        onClick={props.isDisabled ? undefined : () => props.onSelectDate?.(props.date)}
        onMouseEnter={() => props.onHover?.(props.date)}
        onFocus={() => props.onFocus?.(props.date)}
        tabIndex={-1}
        role="gridcell"
        aria-label={formatDate('dateFull', props.date)}
        aria-selected={isSelected ? true : undefined}
        aria-disabled={props.isDisabled ? true : undefined}
      >
        <Text size="small" alternative={isSelected || isToday} color={color}>
          {String(props.date.getDate())}
        </Text>
      </div>
    </GridItem>
  );
}

const dayVariants = cva(
  'flex w-full flex-col items-center justify-center rounded-[var(--radius-small)] py-2 [outline:none] [outline-offset:1px] focus:[outline:2px_solid_var(--color-palettes-blue-60-100)]',
  {
    variants: {
      background: {
        differentMonth: 'bg-[var(--color-white)]',
        selected: 'bg-[var(--color-palettes-blue-60-100)]',
        highlighted: 'bg-[var(--color-palettes-blue-10-100)]',
        default: '',
      },
      isDisabled: {
        true: 'cursor-default',
        false: 'cursor-pointer',
      },
      isFocused: {
        true: '[outline:2px_solid_var(--color-palettes-blue-60-100)]',
        false: '',
      },
      isHoverable: {
        true: 'hover:bg-[var(--color-palettes-blue-10-100)]',
        false: '',
      },
    },
  }
);
